import { api } from './client';
import {
  SignupInput,
  LoginInput,
  User,
  AuthResponse,
  UserWorkspace,
  RequestPasswordResetInput,
  ConfirmPasswordResetInput,
  PasswordResetRequestResponse,
  PasswordResetConfirmResponse,
} from '@altsui/shared';

export interface EnhancedSignupInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export const authApi = {
  signup: async (input: SignupInput): Promise<User> => {
    const data = await api.post<{ user: User }>('/auth/signup', input);
    return data.user;
  },

  /**
   * Enhanced signup for fund managers creating a new account
   * Creates user with onboarding_completed = false
   * Returns auth tokens so user can proceed to fund creation wizard
   */
  enhancedSignup: async (input: EnhancedSignupInput): Promise<AuthResponse> => {
    return api.post<AuthResponse>('/auth/enhanced-signup', input);
  },

  login: async (input: LoginInput): Promise<AuthResponse> => {
    return api.post<AuthResponse>('/auth/login', input);
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout', {});
  },

  getCurrentUser: async (): Promise<User> => {
    return api.get<User>('/auth/me');
  },

  getWorkspaces: async (): Promise<UserWorkspace[]> => {
    return api.get<UserWorkspace[]>('/auth/workspaces');
  },

  refreshToken: async (): Promise<{ refreshed: boolean }> => {
    return api.post<{ refreshed: boolean }>(
      '/auth/refresh',
      {},
    );
  },

  /** With an invite token, a Google account whose email is not the invited one is refused before any account exists. */
  googleAuth: async (credential: string, inviteToken?: string): Promise<AuthResponse> => {
    return api.post<AuthResponse>('/auth/google', inviteToken ? { credential, inviteToken } : { credential });
  },

  requestPasswordReset: async (
    input: RequestPasswordResetInput,
  ): Promise<PasswordResetRequestResponse> => {
    return api.post<PasswordResetRequestResponse>('/auth/forgot-password', input);
  },

  resetPassword: async (
    input: ConfirmPasswordResetInput,
  ): Promise<PasswordResetConfirmResponse> => {
    return api.post<PasswordResetConfirmResponse>('/auth/reset-password', input);
  },
};

